import { useEffect, useState } from 'react';

/** Light / dark toggle.
 *
 *  **The stored value is an explicit choice, and it wins.** There is no
 *  `prefers-color-scheme` block in the CSS on purpose: a media query would fight
 *  the attribute this writes, and somebody on a dark OS who picked light would
 *  get dark back on the next paint. The system preference is read once, as the
 *  starting point on a first visit, and after that the choice is theirs.
 *
 *  The attribute goes on <html> rather than a wrapper, so it also covers
 *  anything portalled outside the app root and the browser's own chrome for
 *  form controls and scrollbars — `color-scheme` hangs off the same selector.
 */

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'mailgiri.theme';

function stored(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'dark' || value === 'light' ? value : null;
  } catch {
    // Throws outright in a private window and in some embedded views.
    return null;
  }
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
  );
}

export function initialTheme(): Theme {
  return stored() ?? (systemPrefersDark() ? 'dark' : 'light');
}

/** Applied outside React too — see main.tsx — so the first paint is already the
 *  right theme. A flash of the wrong one is worse than a slow toggle. */
export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
}

export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  function toggle() {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The theme still applies for this session; only the memory is lost.
    }
  }

  return (
    <button
      type="button"
      className="topbar-icon"
      onClick={toggle}
      // Says what it will do, not what it currently is. "Dark theme" as a label
      // on a button already in dark mode is ambiguous read aloud.
      aria-label={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
      title={theme === 'dark' ? 'Light theme' : 'Dark theme'}
    >
      <span aria-hidden="true">{theme === 'dark' ? '☀' : '☾'}</span>
    </button>
  );
}
